import React, { useState, useEffect } from 'react';
import { Input, List, Spin, Typography, Divider, Statistic, message, Drawer, Button } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import axios from 'axios';
import MeasurementSelector from './MeasurementSelector';
import PeriodDropdown from './PeriodDropdown';
import TrackFoodButton from './TrackFoodButton';
import AddFoodModal from './AddFood';

const { Search } = Input;
const { Title, Paragraph } = Typography;
  

// Helper to convert "HH:mm" to minutes since midnight
const timeStringToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

// Select current period ID based on current time and periods list
const getCurrentPeriodId = (periods) => {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  for (const period of periods) {
    if (period.timeslotFrom && period.timeslotTo) {
      const from = timeStringToMinutes(period.timeslotFrom);
      const to = timeStringToMinutes(period.timeslotTo);

      if (to >= from) {
        if (currentMinutes >= from && currentMinutes <= to) return period.id;
      } else {
        // crosses midnight
        if (currentMinutes >= from || currentMinutes <= to) return period.id;
      }
    }
  }
  return null;
};

const FoodSearch = ({ onSelect, onNewItemTracked }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const [selectedQuantityType, setSelectedQuantityType] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(null);
  const [quantityInputValue, setQuantityInputValue] = useState('');
  const [portions, setPortions] = useState(null);

  const [calValue, setCalValue] = useState(0);
  const [trackedItems, setTrackedItems] = useState([]);

  const [messageApi, contextHolder] = message.useMessage();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const openModal = () => setModalVisible(true);
  const closeModal = () => setModalVisible(false);

  const [searches, setSearches] = useState(0);


  // Detect screen width for mobile UI, update on resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch periods once on mount and auto-select current period
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/period`, { withCredentials: true })
      .then(resp => {
        setPeriods(resp.data);
        const autoPeriod = getCurrentPeriodId(resp.data);
        setSelectedPeriod(autoPeriod);
      })
      .catch(err => {
        console.error('Failed to load periods', err);
      });
  }, []);

  // Debounced food search effect
  useEffect(() => {
    if (!query) {
      setResults([]);
      setSearchLoading(false);
      setSelectedItem(null);
      return;
    }

    const id = setTimeout(() => {
      setSearchLoading(true);
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/foods/search?q=${encodeURIComponent(query)}`, { withCredentials: true })
        .then(resp => setResults(resp.data))
        .then(setSearches(prev => prev + 1))
        .catch(() => setResults([]))
        .finally(() => setSearchLoading(false));
    }, 500);

    return () => clearTimeout(id);
  }, [query]);

  // Handle food item selection
  const handleSelect = (item) => {
    setSelectedItem(item);
    onSelect?.(item);
    updateFoodOnServer(item);

    // Reset quantities to defaults
    setSelectedQuantityType('grams');
    setSelectedQuantity(100);
    setQuantityInputValue('');
    setPortions(null);

    // Set period based on current time from loaded periods
    const autoPeriod = getCurrentPeriodId(periods);
    setSelectedPeriod(autoPeriod);

    if (isMobile) setDrawerVisible(true);
  };

  // Ping Backend with selection
  const updateFoodOnServer = (item) => {
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/ai/food-nutrition?foodId=${encodeURIComponent(
          item.id
        )}`,
        { withCredentials: true }
      )
      .then((resp) => {
        const updatedItem = resp.data; // assuming updated item is in resp.data

        setResults((prevResults) =>
          prevResults.map((r) => (r.id === updatedItem.id ? updatedItem : r))
        );
      })
      .catch((error) => {
        console.error("Failed to update food:", error);
        // Optional: handle error, e.g. show message
      });
  };

  // Calculate calories on quantity/unit/food changes
  useEffect(() => {
    if (!selectedItem || !selectedQuantityType) {
      setCalValue(0);
      return;
    }

    const unitMultipliers = {
      grams: (val) => (val / 100) * selectedItem.calories,
      ml: (val) => val * selectedItem.mlpergcf * selectedItem.calories/100,
      cup: (val) => val * selectedItem.mlpergcf * 250 * selectedItem.calories/100,
      tbsp: (val) => val * selectedItem.mlpergcf * 15 * selectedItem.calories/100,
      tsp: (val) => (val / 100) * selectedItem.mlpergcf * 5 * selectedItem.calories,
      item: (val) => val * selectedItem.onepergcf * selectedItem.calories
    };

    const val = selectedQuantity === 'Other' ? parseFloat(quantityInputValue) : Number(selectedQuantity);
    const validVal = isNaN(val) ? 0 : val;

    const cals = unitMultipliers[selectedQuantityType]
      ? unitMultipliers[selectedQuantityType](validVal)
      : selectedItem.calories;

    setCalValue(isNaN(cals) ? 0 : cals);
  }, [selectedQuantityType, selectedQuantity, quantityInputValue, selectedItem]);

  const handleMeasurementChange = ({ unit, value }) => {
    setSelectedQuantityType(unit);
    if (value === 'Other') {
      setSelectedQuantity('Other');
    } else {
      setSelectedQuantity(value);
      setQuantityInputValue('');
    }
  };

const handleFoodAdded = (newFood) => {
  setResults((prevResults) => [newFood, ...prevResults]);
  setSelectedItem(newFood);
  closeModal();
  messageApi(`Added new food: ${newFood.name}`);
  
};


  const handleQuantityInputChange = (e) => {
    setQuantityInputValue(e.target.value);
  };

    const fetchRecentTrackedFoods = async () => {
    setSearchLoading(true);
    try {
      const resp = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/track-foods/recent`, { withCredentials: true });
      setResults(resp.data); // Set recent foods as result list
    } catch (err) {
      console.error('Failed to fetch recent tracked foods:', err);
      setResults([]);
    } finally {
      setSearchLoading(false);
      setSelectedItem(null);
    }
  };

  useEffect(() => {
    fetchRecentTrackedFoods();
  }, []);

    useEffect(() => {
    if (!query) {
      fetchRecentTrackedFoods();
      return;
    }

    const id = setTimeout(() => {
      setSearchLoading(true);
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/foods/search?q=${encodeURIComponent(query)}`, { withCredentials: true })
        .then(resp => setResults(resp.data))
        .then(setSearches(prev => prev + 1))
        .catch(() => setResults([]))
        .finally(() => setSearchLoading(false));
    }, 500);

    return () => clearTimeout(id);
  }, [query]);


  const buildFoodLogModel = () => {
    if (!selectedItem || !selectedQuantityType) return null;
    const quantity = selectedQuantity === 'Other' ? Number(quantityInputValue) : Number(selectedQuantity);
    if (isNaN(quantity) || quantity <= 0) return null;

    return {
      trackDateTime: new Date().toISOString(),
      foodId: selectedItem.id,
      quantity,
      quantityType: selectedQuantityType,
      recipeId: null,
      portions,
      periodId: selectedPeriod,
    };
  };

  const openDrawer = () => setDrawerVisible(true);
  const closeDrawer = () => setDrawerVisible(false);

  const leftPanel = (
    <div style={{
      width: isMobile ? '100%' : '30%',
      maxHeight: '100%',
      overflowY: 'auto',
      border: '1px solid #d9d9d9',
      borderRadius: 6,
      backgroundColor: '#fff',
      position: 'relative'
    }}>
      <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: '#fff',
        padding: 12,
        zIndex: 10,
        borderBottom: '1px solid #d9d9d9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Search
          placeholder="Search for a food item"
          loading={searchLoading}
          enterButton
          value={query}
          onChange={e => setQuery(e.target.value)}
          allowClear
        />
        {isMobile && selectedItem && (
          <Button
            icon={<MenuOutlined />}
            type="text"
            onClick={openDrawer}
            aria-label="Open food details"
          />
        )}
      </div>
<div style={{ padding: '8px 12px' }}>
  {searchLoading ? (
    <Spin />
  ) : (
    <>
      <List
        dataSource={results}
        renderItem={(item) => (
          <List.Item
            key={item.id}
            style={{
              cursor: 'pointer',
              backgroundColor: selectedItem === item ? '#e6f7ff' : 'transparent',
            }}
            onClick={() => handleSelect(item)}
          >
            {item.name}
          </List.Item>
        )}
        locale={{ emptyText: 'No results' }}
      />
      {(searches > 0 && (results.length === 0 || results.length < 5)) && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button type="primary" onClick={openModal}>
            Add Food Item
          </Button>
        </div>
      )}
    </>
  )}
</div>

       
    </div>
  );

  const rightPanelContent = selectedItem ? (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>{selectedItem.name}</Title>
        <Statistic title="Calories (kcal)" value={calValue} precision={0} style={{ textAlign: 'right' }} />
      </div>
      <Divider />
      <MeasurementSelector
        mlpergcf={selectedItem.mlpergcf}
        onepergcf={selectedItem.onepergcf}
        onChange={handleMeasurementChange}
      />
      {selectedQuantity === 'Other' && (
        <Input
          style={{ marginTop: 40, width: '100%' }}
          placeholder="Enter custom quantity"
          type="number"
          min={0}
          step={0.01}
          value={quantityInputValue}
          onChange={handleQuantityInputChange}
        />
      )}
      <div style={{ marginTop: 16 }}>
        <PeriodDropdown
          periods={periods}
          value={selectedPeriod}
          onChange={setSelectedPeriod}
        />
      </div>
      <div style={{ marginTop: 16 , marginBottom: 16}}>
<TrackFoodButton
  foodLogModel={buildFoodLogModel()}
  onTrackedItemsChange={setTrackedItems}
  messageApi={messageApi}
  onTrackSuccess={() => {
    if (onNewItemTracked) onNewItemTracked();
    if (isMobile) closeDrawer(); // <-- This will close the drawer on successful item track (mobile only)
  }}
/>

      </div>
    <Paragraph>Description: {selectedItem.description ?? 'No description available.'}</Paragraph>
   <Paragraph>
  Calories per 100g: {selectedItem.calories != null ? `${Math.round(selectedItem.calories)} kcal` : 'N/A'}
  </Paragraph>
    </>
  ) : (
    <Paragraph>Select an item from the list to see details here.</Paragraph>
  );

  return (
    <>
      {contextHolder}
      <div style={{ display: 'flex', gap: 24, height: '70vh', width: '100%' }}>

              <AddFoodModal
        visible={modalVisible}
        onClose={closeModal}
        onAdded={handleFoodAdded}
        foodName={query}
      />

        {leftPanel}

        {isMobile ? (
          <Drawer
            title={selectedItem?.name ?? 'Food Details'}
            placement="right"
            width="100%"
            onClose={closeDrawer}
            open={drawerVisible}
            destroyOnClose
          >
            {rightPanelContent}
          </Drawer>
        ) : (
          <div style={{
            flex: 1,
            maxHeight: '100%',
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            padding: 24,
            backgroundColor: '#fff',
          }}>
            {rightPanelContent}
          </div>
        )}
      </div>
    </>
  );
};

export default FoodSearch;
